import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { degreesAPI } from '../services/api';

const EditDegreeProgram = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [degreePrograms, setDegreePrograms] = useState([]);
  const [selectedDegreeId, setSelectedDegreeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  
  const [formData, setFormData] = useState({
    degreeProgrammeName: '',
    degreeLevel: '',
    localInternational: '',
    duration: 4,
    description: '',
    status: 'Active'
  });

  // Fetch degree programs on component mount
  useEffect(() => {
    if (currentUser) {
      fetchDegreePrograms();
    }
  }, [currentUser]);

  const fetchDegreePrograms = async () => {
    try {
      setLoadingPrograms(true);
      console.log('Fetching degree programs from API...');
      
      const response = await degreesAPI.getAllDegrees();
      console.log('API Response:', response.data);
      
      if (response.data && Array.isArray(response.data)) {
        setDegreePrograms(response.data);
        console.log('Degree programs loaded:', response.data.length);
      } else {
        console.warn('No degree programs found or invalid response format');
        setDegreePrograms([]);
      }
    } catch (error) {
      console.error('Error fetching degree programs:', error);
      alert('Failed to load degree programs. Please try again.');
      setDegreePrograms([]);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const handleDegreeSelection = async (degreeId) => {
    if (!degreeId) {
      // Clear form if no degree selected
      setFormData({
        degreeProgrammeName: '',
        degreeLevel: '',
        localInternational: '',
        duration: 4,
        description: '',
        status: 'Active'
      });
      setSelectedDegreeId('');
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching degree details for ID:', degreeId);
      
      const response = await degreesAPI.getDegreeById(degreeId);
      console.log('Degree details response:', response.data);
      
      if (response.data) {
        const degree = response.data;
        setFormData({
          degreeProgrammeName: degree.degree_programme || '',
          degreeLevel: degree.degree_level || '',
          localInternational: degree.local_international || '',
          duration: degree.duration_years || 4,
          description: degree.description || '',
          status: degree.active ? 'Active' : 'Inactive'
        });
        setSelectedDegreeId(degreeId);
        console.log('Form populated with degree data');
      }
    } catch (error) {
      console.error('Error fetching degree details:', error);
      alert('Failed to load degree details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDegreeId) {
      alert('Please select a degree program to edit');
      return;
    }

    // Validate required fields
    if (!formData.degreeProgrammeName) {
      alert('Degree Programme Name is required');
      return;
    }

    if (!formData.degreeLevel) {
      alert('Degree Level is required');
      return;
    }

    if (!formData.localInternational) {
      alert('Local/International selection is required');
      return;
    }

    try {
      setLoading(true);

      // Prepare degree program data for update
      const degreeData = {
        degree_programme: formData.degreeProgrammeName,
        degree_level: formData.degreeLevel.toLowerCase(),
        local_international: formData.localInternational.toLowerCase(),
        duration_years: parseInt(formData.duration),
        description: formData.description || null,
        active: formData.status === 'Active'
      };

      console.log('Updating degree program with data:', degreeData);

      // Update the degree program via API
      const response = await degreesAPI.updateDegree(selectedDegreeId, degreeData);
      console.log('Update response:', response.data);
      
      alert('Degree Program updated successfully!');
      navigate('/academic-management');

    } catch (error) {
      console.error('Error updating degree program:', error);
      let errorMessage = 'Error updating degree program: ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error.details) {
        errorMessage += error.details;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {!currentUser ? (
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Please Log In
            </h2>
            <p style={{fontSize: 16, fontFamily: 'Outfit', color: '#77838F'}}>
              You need to be logged in as an admin to edit degree programs.
            </p>
          </div>
        ) : (
        <>
        {/* Page Title */}
        <h1 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', marginTop: '20px', paddingLeft: '40px'}}>
          Edit Degree Program
        </h1>

        {/* Edit Degree Program Content Area */}
        <form onSubmit={handleSubmit}>
          {(loading || loadingPrograms) && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: 'white',
                padding: '20px 40px',
                borderRadius: 8,
                textAlign: 'center',
                fontSize: 16,
                fontFamily: 'Outfit',
                color: '#F04915'
              }}>
                {loadingPrograms ? 'Loading degree programs...' : 'Updating degree program...'}
              </div>
            </div>
          )}
          
          <div style={{marginTop: '20px', margin: '20px 40px'}}>
            <div style={{background: '#F04915', padding: '20px', borderTopLeftRadius: 15, borderTopRightRadius: 15}}>
              <h2 style={{color: 'white', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', margin: 0}}>
                Edit Degree Program Details
              </h2>
            </div>

            <div style={{
              background: '#FFFBF5', 
              padding: '40px',
              borderBottomLeftRadius: 15,
              borderBottomRightRadius: 15
            }}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* Left Column */}
                <div>
                  {/* Degree Program Selection Dropdown */}
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Select Degree Programme <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      value={selectedDegreeId}
                      onChange={(e) => handleDegreeSelection(e.target.value)}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select a Degree Programme</option>
                      {degreePrograms.map((degree) => (
                        <option key={degree.id} value={degree.id}>
                          {degree.degree_programme} ({degree.local_international === 'local' ? 'Local' : 'International'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Degree Programme Name <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="degreeProgrammeName"
                      type="text"
                      placeholder="Software Engineering"
                      value={formData.degreeProgrammeName}
                      onChange={handleInputChange}
                      required
                      disabled={!selectedDegreeId}
                      style={{
                        width: '100%', 
                        height: 49, 
                        padding: '0 15px', 
                        borderRadius: 8, 
                        border: '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        backgroundColor: !selectedDegreeId ? '#f8f9fa' : 'white',
                        cursor: !selectedDegreeId ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Degree Level <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="degreeLevel"
                      value={formData.degreeLevel}
                      onChange={handleInputChange}
                      required
                      disabled={!selectedDegreeId}
                      style={{
                        width: '100%', 
                        height: 49, 
                        padding: '0 15px', 
                        borderRadius: 8, 
                        border: '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        backgroundColor: !selectedDegreeId ? '#f8f9fa' : 'white',
                        cursor: !selectedDegreeId ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value="">Select Degree Level</option>
                      <option value="undergraduate">Undergraduate</option>
                      <option value="postgraduate">Postgraduate</option>
                      <option value="doctoral">Doctoral</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Duration (Years) <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="duration"
                      type="number"
                      min="1"
                      max="8"
                      value={formData.duration}
                      onChange={handleInputChange}
                      required
                      disabled={!selectedDegreeId}
                      style={{
                        width: '100%', 
                        height: 49, 
                        padding: '0 15px', 
                        borderRadius: 8, 
                        border: '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        backgroundColor: !selectedDegreeId ? '#f8f9fa' : 'white',
                        cursor: !selectedDegreeId ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Local/International <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="localInternational"
                      value={formData.localInternational}
                      onChange={handleInputChange}
                      required
                      disabled={!selectedDegreeId}
                      style={{
                        width: '100%', 
                        height: 49, 
                        padding: '0 15px', 
                        borderRadius: 8, 
                        border: '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        backgroundColor: !selectedDegreeId ? '#f8f9fa' : 'white',
                        cursor: !selectedDegreeId ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value="">Select Type</option>
                      <option value="local">Local</option>
                      <option value="international">International</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Status <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      disabled={!selectedDegreeId}
                      style={{
                        width: '100%', 
                        height: 49, 
                        padding: '0 15px', 
                        borderRadius: 8, 
                        border: '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        backgroundColor: !selectedDegreeId ? '#f8f9fa' : 'white',
                        cursor: !selectedDegreeId ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Description - Full Width */}
                <div style={{gridColumn: '1 / -1', marginTop: '20px'}}>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    placeholder="Enter degree program description..."
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="4"
                    disabled={!selectedDegreeId}
                    style={{
                      width: '100%', 
                      padding: '15px', 
                      borderRadius: 8, 
                      border: '1px solid #77838F', 
                      fontSize: 16, 
                      fontFamily: 'Outfit',
                      resize: 'vertical',
                      minHeight: '100px',
                      backgroundColor: !selectedDegreeId ? '#f8f9fa' : 'white',
                      cursor: !selectedDegreeId ? 'not-allowed' : 'text'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{display: 'flex', justifyContent: 'space-between', margin: '40px'}}>
            <Link to="/academic-management">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back to Academic Management
              </button>
            </Link>
            <button 
              type="submit" 
              disabled={!selectedDegreeId}
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: !selectedDegreeId ? '#6C757D' : '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: !selectedDegreeId ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                if (selectedDegreeId) {
                  e.target.style.background = '#D63A0F';
                }
              }}
              onMouseOut={(e) => {
                if (selectedDegreeId) {
                  e.target.style.background = '#F04915';
                }
              }}
            >
              Update Degree Program
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default EditDegreeProgram;
